import {
  DEBT_REMINDER_TOKENS,
  DEFAULT_DEBT_REMINDER_BODY,
  DEFAULT_DEBT_REMINDER_SUBJECT,
} from '@cpce/shared';
import type { Decimal } from 'decimal.js';
import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSendDebtReminders } from '@/api/employees/employees.mutations';
import type { PrestationParticipationFormatted } from '@/api/prestations/prestations.types';
import { Button } from '@/components/Button';
import { FieldLabel } from '@/components/form/FieldLabel';
import { InfoCard } from '@/components/InfoCard';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Table } from '@/components/table/Table';
import { useTableState } from '@/components/table/useTableState';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import { debtsColumns } from '../columns/debts-columns';

const DEBT_REMINDERS_INITIAL_SORTING = [{ id: 'lastName', desc: false }];

interface DebtRemindersModalProps {
  open: boolean;
  closeModal: () => void;
  participationIds: number[];
  employeeCount: number;
  total: Decimal;
  /** Detail of the selected debts. Omitted when relancing a salarié from their own fiche. */
  rows?: PrestationParticipationFormatted[];
}

export function DebtRemindersModal(props: DebtRemindersModalProps) {
  const { open, closeModal, participationIds, employeeCount, total, rows } = props;
  const { sendDebtReminders, isPending } = useSendDebtReminders();
  const [subject, setSubject] = useState(DEFAULT_DEBT_REMINDER_SUBJECT);
  const [body, setBody] = useState(DEFAULT_DEBT_REMINDER_BODY);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Reopening starts from the default wording: a text half-edited for a previous selection
  // would otherwise go out, unchanged and unnoticed, to a different set of salariés.
  useEffect(() => {
    if (open) {
      setSubject(DEFAULT_DEBT_REMINDER_SUBJECT);
      setBody(DEFAULT_DEBT_REMINDER_BODY);
    }
  }, [open]);

  const tableState = useTableState('debt-reminders', {
    columns: debtsColumns,
    initialSorting: DEBT_REMINDERS_INITIAL_SORTING,
    persist: false,
  });

  const insertToken = (token: string) => {
    const textarea = bodyRef.current;
    const placeholder = `[${token}]`;
    if (!textarea) {
      setBody((previous) => previous + placeholder);
      return;
    }
    const { selectionStart, selectionEnd } = textarea;
    setBody(
      (previous) => previous.slice(0, selectionStart) + placeholder + previous.slice(selectionEnd)
    );
    // Put the caret back after the token, once React has written the new value.
    requestAnimationFrame(() => {
      const caret = selectionStart + placeholder.length;
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    });
  };

  const hasInvalidSubjectChar = /[<>]/.test(subject);
  const isValid = subject.trim().length > 0 && body.trim().length > 0 && !hasInvalidSubjectChar;

  return (
    <Modal open={open} onOpenChange={closeModal} title="Relance de bénéficiaires">
      <InfoCard title="Confirmation de relance" color="blue" icon={Info}>
        <p className="text-sm text-gray-700">
          Un email sera envoyé à chaque bénéficiaire concerné, qui reprend le total de ses dettes
          sélectionnées ici. Cette action sera enregistrée dans l'historique de relance de chaque
          bénéficiaire.
        </p>
      </InfoCard>

      <span className="text-gray-900 font-medium">Bénéficiaires concernés ({employeeCount})</span>

      {rows && (
        <Table
          entity={{ singular: 'dette', feminine: true }}
          data={rows}
          columns={debtsColumns}
          tableState={tableState}
        />
      )}

      <div className="text-gray-900 font-bold text-right text-2xl">
        Total: <span className="text-red-600">{formatDecimalAsPrice(total)}</span>
      </div>

      <FieldLabel label="Objet">
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Objet de l'email"
        />
      </FieldLabel>
      {hasInvalidSubjectChar && (
        <p className="text-sm text-red-600 -mt-2">
          L'objet ne peut pas contenir les caractères &lt; ou &gt;.
        </p>
      )}

      <FieldLabel label="Message">
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          className="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
        />
      </FieldLabel>

      <div className="flex items-center gap-2 flex-wrap -mt-2">
        <span className="text-sm text-gray-600">Insérer :</span>
        {DEBT_REMINDER_TOKENS.map((token) => (
          <Button
            key={token}
            variant="outline"
            className="text-xs py-1 px-2"
            onClick={() => insertToken(token)}
          >
            [{token}]
          </Button>
        ))}
        <span className="text-xs text-gray-500">
          remplacé par les informations de chaque bénéficiaire
        </span>
      </div>

      <div className="flex gap-2 grow">
        <Button variant="outline" className="grow" onClick={closeModal}>
          Annuler
        </Button>
        <Button
          variant="color"
          className="bg-pink-700 grow"
          isPending={isPending}
          disabled={!isValid}
          onClick={async () => {
            await sendDebtReminders({ participationIds, subject, body });
            closeModal();
          }}
        >
          Confirmer l'envoi
        </Button>
      </div>
    </Modal>
  );
}
